import React, { useContext, useState } from 'react';
import { FaLock, FaKey, FaExclamationCircle, FaShieldAlt, FaCheckCircle} from 'react-icons/fa';
import { MdSecurity } from 'react-icons/md';
import { Spin } from 'antd';
import { toast } from 'sonner';
import Auth from './Auth';
import apiUtils from 'utils/apiutils';
import { OtpInput } from './ui/OtpInput';
import { PasswordInput } from './ui/PasswordInput';
import { Modal } from './ui/Modal';
import { DataContext } from 'context/DataContext';
import TelegramAlerts from './TelegramAlerts';

const SecuritySettings = () => {
    const { fetchProfileData, profileData } = useContext(DataContext);
    
    const [state, setState] = useState({
        showPasswordModal: false,
        showTxPasswordModal: false,
        showOtpModal: false,
        showOtpTpModal: false,
        newPassword: '',
        oldPassword: '',
        confirmPassword: '',
        txPassword: '',
        otp: '',
        otpTp: '',
        error: '',
        showNewPassword: false,
        showConfirmPassword: false,
        showTxPassword: false,
        loadingPass: false,
        loadingTpPass: false,
    });

    const updateState = (updates) => setState((prev) => ({ ...prev, ...updates }));

    const resetPasswordForm = () => updateState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        otp: '',
        error: '',
        showNewPassword: false,
        showConfirmPassword: false,
        showPasswordModal: false,
        showOtpModal: false,
    });

    const resetTxPasswordForm = () => updateState({
        txPassword: '',
        otpTp: '',
        error: '',
        showTxPassword: false,
        showTxPasswordModal: false,
        showOtpTpModal: false,
    });

    const handleApiError = (error) => {
        console.error(error);
        toast.error(error?.response?.data?.Error || 'An error occurred');
        updateState({ error: '', loadingPass: false, loadingTpPass: false });
    };

    const handleSendOtp = async () => {
        try {
            const response = await apiUtils.post('/account/reset_password_email', { email: profileData?.Profile?.Email });
            if (response.Success) {
                updateState({ showPasswordModal: false, showOtpModal: true });
                toast.success(response.Success);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleUpdatePassword = async () => {
        updateState({ loadingPass: true });
        try {
            const response = await apiUtils.post('/user/change_password', {
                oldpassword: state.oldPassword,
                newpassword: state.newPassword,
                conpassword: state.confirmPassword,
            }, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
            if (response.Success) {
                updateState({ showOtpModal: false, loadingPass: false });
                resetPasswordForm();
                toast.success(response.Success);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePasswordUpdate = () => {
        if (!state.oldPassword || !state.newPassword || !state.confirmPassword) {
            return updateState({ error: 'All fields are required' });
        }
        if (state.newPassword !== state.confirmPassword) {
            return updateState({ error: 'Passwords do not match' });
        }
        if (state.newPassword.length < 8) {
            return updateState({ error: 'Password must be at least 8 characters long' });
        }
        handleUpdatePassword();
    };

    const handleSendOtpTrans = async () => {
        if (!state.txPassword) return toast.error('Enter Transaction Password');
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!strongPasswordRegex.test(state.txPassword)) {
            return toast.error('Password must be at least 8 characters long, include uppercase, lowercase, and a number.');
        }
        try {
            const response = await apiUtils.post('/account/send_transaction_password_otp', { email: profileData?.Profile?.Email }, { showToast: true });
            if (response.Success) {
                updateState({ showTxPasswordModal: false, showOtpTpModal: true });
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleUpdateTransPass = async () => {
        if (!state.otpTp) return toast.error('Enter OTP of 6 Digits');
        updateState({ loadingTpPass: true });
        try {
            const response = await apiUtils.post('/account/set_transaction_password', {
                password: state.txPassword,
                otp: state.otpTp,
                project: '',
            }, { showToast: false });
            if (response.Success) {
                updateState({ showOtpTpModal: false, loadingTpPass: false });
                resetTxPasswordForm();
                fetchProfileData();
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <div className="py-8">
            <div className="m-3">
                <div className="glass-card rounded-xl overflow-hidden hover-scale">
                    <div className="bg-zinc-700 p-4">
                        <div className="flex items-center space-x-3">
                            <div className="p-3 bg-white bg-opacity-20 rounded-full">
                                <MdSecurity className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-white">Security Settings</h2>
                        </div>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="flex items-start space-x-3 p-4 bg-blue-900 bg-opacity-20 rounded-lg border border-blue-800">
                            <FaShieldAlt className="w-5 h-5 text-blue-400 mt-1" />
                            <div className="flex-1">
                                <div className="flex justify-between">
                                    <h4 className="font-medium text-blue-300">Account Protection</h4>
                                    <div className="flex items-center gap-3 font-bold text-neonPurple">
                                        <p className="text-lg">2FA</p>
                                        <Auth />
                                    </div>
                                </div>
                                <p className="text-sm text-gray-400 mt-1">Secure your account with Two Factor Authentication (2FA).</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <button
                                onClick={() => updateState({ showPasswordModal: true })}
                                className="w-full bg-gray-800 bg-opacity-50 text-white py-4 px-6 rounded-lg font-medium hover:bg-opacity-70 transition-all flex items-center space-x-3"
                            >
                                <div className="p-2 bg-neonGreenDark bg-opacity-30 rounded-full">
                                    <FaKey className="w-4 h-4 text-neonPurple" />
                                </div>
                                <div className="text-left">
                                    <span className="block">Change Password</span>
                                    <span className="text-xs text-gray-400">Update your account password</span>
                                </div>
                            </button>
                            {/* <button
                                onClick={() => updateState({ showTxPasswordModal: true })}
                                className="w-full bg-gray-800 bg-opacity-50 text-white py-4 px-6 rounded-lg font-medium hover:bg-opacity-70 transition-all flex items-center space-x-3"
                            >
                                <div className="p-2 bg-neonGreenDark bg-opacity-30 rounded-full">
                                    <FaLock className="w-4 h-4 text-neonPurple" />
                                </div>
                                <div className="text-left">
                                    <span className="block">
                                        {profileData?.Transaction_Password_Set ? 'Reset Transaction Password' : 'Create Transaction Password'}
                                    </span>
                                    <span className="text-xs text-gray-400">Required for withdrawals</span>
                                </div>
                            </button> */}
                            <div className="p-4 bg-gray-800 bg-opacity-30 rounded-lg">
                                <h4 className="font-medium text-gray-300 mb-2">Security Tips</h4>
                                <ul className="text-sm text-gray-400 space-y-2">
                                    {['Use unique passwords for each platform', 'Enable two-factor authentication'].map((tip) => (
                                        <li key={tip} className="flex items-start space-x-2">
                                            <FaCheckCircle className="w-4 h-4 text-neonPurple mt-0.5" />
                                            <span>{tip}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div>

                            <TelegramAlerts/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <Modal
                isOpen={state.showPasswordModal}
                onClose={resetPasswordForm}
                title="Update Password"
                icon={<FaKey className="mr-3 text-neonPurple" />}
            >
                <div className="space-y-5">
                     <PasswordInput
                        label="Old Password"
                        value={state.oldPassword}
                        onChange={(e) => updateState({ oldPassword: e.target.value, error: '' })}
                        showPassword={state.showOldPassword}
                        toggleShowPassword={() => updateState({ showOldPassword: !state.showOldPassword })}
                    />
                    <PasswordInput
                        label="New Password"
                        value={state.newPassword}
                        onChange={(e) => updateState({ newPassword: e.target.value, error: '' })}
                        showPassword={state.showNewPassword}
                        toggleShowPassword={() => updateState({ showNewPassword: !state.showNewPassword })}
                    />
                    <PasswordInput
                        label="Confirm New Password"
                        value={state.confirmPassword}
                        onChange={(e) => updateState({ confirmPassword: e.target.value, error: '' })}
                        showPassword={state.showConfirmPassword}
                        toggleShowPassword={() => updateState({ showConfirmPassword: !state.showConfirmPassword })}
                    />
                    {state.error && (
                        <div className="text-red-500 text-sm flex items-center">
                            <FaExclamationCircle className="w-4 h-4 mr-1" />
                            {state.error}
                        </div>
                    )}
                    <div className="bg-indigo-900 bg-opacity-30 border border-indigo-800 rounded-lg p-3 flex items-start space-x-3">
                        <FaExclamationCircle className="w-5 h-5 text-indigo-400 mt-0.5" />
                        <p className="text-sm text-gray-300">
                            Password must be at least 8 characters long and include a mix of letters, numbers, and special characters.
                        </p>
                    </div>
                    <div className="flex space-x-4 pt-2">
                        <button
                            onClick={handlePasswordUpdate}
                            className="flex-1 bg-primary  text-secondary py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center"
                        >
                            Update Password
                        </button>
                        <button
                            onClick={resetPasswordForm}
                            className="flex-1 bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* OTP Modal for Password */}
            <Modal
                isOpen={state.showOtpModal}
                onClose={resetPasswordForm}
                title="Update Password"
                icon={<FaKey className="mr-3 text-neonPurple" />}
            >
                <div className="space-y-5">
                    <OtpInput
                        label="Enter OTP (One Time Password)"
                        value={state.otp}
                        onChange={(val) => updateState({ otp: val, error: '' })}
                    />
                    <div className="flex space-x-4 pt-2">
                        <button
                            onClick={handleUpdatePassword}
                            className="flex-1  bg-primary text-secondary py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center"
                            disabled={state.loadingPass}
                        >
                            {state.loadingPass ? <Spin size="small" /> : 'Update Password'}
                        </button>
                        <button
                            onClick={resetPasswordForm}
                            className="flex-1 bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Transaction Password Modal */}
            <Modal
                isOpen={state.showTxPasswordModal}
                onClose={resetTxPasswordForm}
                title={profileData?.Transaction_Password_Set ? 'Reset Transaction Password' : 'Create Transaction Password'}
                icon={<FaLock className="mr-3 text-neonPurple" />}
            >
                <div className="space-y-5">
                    <PasswordInput
                        label={profileData?.Transaction_Password_Set ? 'Reset Transaction Password' : 'Transaction Password'}
                        value={state.txPassword}
                        onChange={(e) => updateState({ txPassword: e.target.value, error: '' })}
                        showPassword={state.showTxPassword}
                        toggleShowPassword={() => updateState({ showTxPassword: !state.showTxPassword })}
                        placeholder="Enter transaction password"
                    />
                    <div className="bg-blue-900 bg-opacity-30 border border-blue-800 rounded-lg p-4 flex items-start space-x-3">
                        <FaExclamationCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-medium text-yellow-400">Important Security Notice</h4>
                            <p className="text-sm text-gray-300 mt-1">
                                Your transaction password will be required for all withdrawal requests. Please store it securely and do not share it with anyone.
                            </p>
                        </div>
                    </div>
                    <div className="flex space-x-4 pt-2">
                        <button
                            onClick={handleSendOtpTrans}
                            className="flex-1 bg-logiculustheme-neonBlue text-white py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center"
                        >
                            Get OTP
                        </button>
                        <button
                            onClick={resetTxPasswordForm}
                            className="flex-1 bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* OTP Modal for Transaction Password */}
            <Modal
                isOpen={state.showOtpTpModal}
                onClose={resetTxPasswordForm}
                title={profileData?.Transaction_Password_Set ? 'Reset Transaction Password' : 'Create Transaction Password'}
                icon={<FaLock className="mr-3 text-neonPurple" />}
            >
                <div className="space-y-5">
                    <OtpInput
                        label="Enter OTP (One Time Password)"
                        value={state.otpTp}
                        onChange={(val) => updateState({ otpTp: val, error: '' })}
                    />
                    <div className="flex space-x-4 pt-2">
                        <button
                            onClick={handleUpdateTransPass}
                            className="flex-1 bg-logiculustheme-neonBlue text-white py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center"
                            disabled={state.loadingTpPass}
                        >
                            Submit
                        </button>
                        <button
                            onClick={resetTxPasswordForm}
                            className="flex-1 bg-gray-800 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SecuritySettings;